import * as React from 'react';
import moment from 'moment-timezone';
import { baseOnSalesDiscountPrice, basePrice, determineFree, discountPrice, discountValue } from './pricingCalculations';
import { cx, priceFormat } from './util';
import styles from "./main.module.scss";
import { translateService, Translate } from '../../services/TranslateService';
import "./payment.scss"
import "./PaymentCallbacks.scss"
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import { DataContext } from '../../contexts/dataContext';
import { GlobalStore } from '../../reduxStore';
import { ServiceRow } from "./PaymentInfo";
import Modal from "react-modal";
import {cStylesPaymentCreatedRes} from "../../CustomModalStyles";
import {useClarityStep} from "../../services/clarity/useClarityStep";
import ApiService from "../../services/api/ApiService";
import {useEffect, useState} from "react";
import { WalleyIframePayload } from "../../services/api/walley/WalleyApi";
import useWalleyIframe from "../../hooks/useWalleyIframe";
import { LoadingSpinner } from "../BusyModal/BusyModal";
import { parseRidSession } from "../../interfaces/RidSessionData";


interface PaymentPropsI {
    back: Function;
    setRedirectHome : Function;
    reservationLength : number;
    escapeWarningOpen: boolean;
    setEscapeWarningOpen: Function;
    openSafeDriveInfo: Function;
    apiService: ApiService | undefined;
    setShowPaymentResultScreen: Function;
    showPaymentInProgress: boolean;
    setShowPaymentInProgress: Function;
}

const WALLEY_CONTAINER_ID = 'walley-checkout';

export default function PayCreatedReservation({ setRedirectHome, back, reservationLength,
                                                  escapeWarningOpen, setEscapeWarningOpen, openSafeDriveInfo,
                                                  apiService, setShowPaymentResultScreen,
                                                  showPaymentInProgress,
                                                  setShowPaymentInProgress}: PaymentPropsI) {
    const { services, clearSelections, vehicle, oneWayRes, pointsInEurosToBeApplied, storedCoupon, sendGa4Event,
        changeVirtualPageInfo, searchParameters, loggedIn, reservationCreated, resCancelled, noVatPrices, addSafeDrivePackage } = React.useContext(DataContext);
    // Save-card opt-in flows from the PaymentInfo merged radio (services.saveNewCreditCard)
    // through to /walley/checkout below. When true, api4 issues a Subscribe-profile
    // session so the iframe charges + saves the token in one pass.
    const saveCard = !!services.saveNewCreditCard;
    const dispatch = useDispatch();
    const deviceLanguage = useSelector((s: GlobalStore) => s.chauffeurReducer.deviceLanguage);
    let pricingDataType = noVatPrices ? vehicle?.pricingDataNoVat : vehicle?.pricingData;
    const couponValid = (((storedCoupon?.valid || vehicle?.vehicleData.class === 0) && pricingDataType?.coupon !== null)
        && !vehicle?.onsale && vehicle?.extraOnsales?.extra !== 1
        && !vehicle?.privateData?.private);
    const freeReservation = determineFree(vehicle, noVatPrices, services, reservationLength, couponValid, pointsInEurosToBeApplied);
    const eurosPerExtraKm = import.meta.env.VITE_EUROS_PER_EXTRA_KM ?? '';
    const useTwoFourPoints = sessionStorage.getItem('using24Points') ?? undefined;
    const resData = parseRidSession(sessionStorage.getItem('rid-1'));
    const reservationId: number | undefined = resData?.resId;

    // Walley iframe state — fetched on mount, retried via the in-place button.
    const [walleyPayload, setWalleyPayload] = useState<WalleyIframePayload | null>(null);
    const [walleyError, setWalleyError] = useState<string | null>(null);
    const [walleyLoading, setWalleyLoading] = useState<boolean>(false);
    const [checkoutAttempt, setCheckoutAttempt] = useState<number>(0); // bump to retrigger /checkout

    const PAYMENT_TIMEOUT_SECONDS = 900; // 15 minutes
    const [paymentTimeRemaining, setPaymentTimeRemaining] = useState<number | null>(null);
    const [paymentExpired, setPaymentExpired] = useState(false);
    const paymentExpiredFired = React.useRef(false);

    useEffect(() => {
        const createTime = resData?.resCreateTime;
        if (!createTime) return;

        const calcRemaining = () => {
            const created = moment.tz(createTime, 'Europe/Helsinki');
            const now = moment.tz('Europe/Helsinki');
            const elapsed = now.diff(created, 'seconds');
            return Math.max(PAYMENT_TIMEOUT_SECONDS - elapsed, 0);
        };

        const remaining = calcRemaining();
        if (remaining <= 0) {
            setPaymentExpired(true);
            setPaymentTimeRemaining(0);
            return;
        }

        setPaymentTimeRemaining(remaining);

        const interval = setInterval(() => {
            const secs = calcRemaining();
            setPaymentTimeRemaining(secs);
            if (secs <= 0) {
                clearInterval(interval);
                setPaymentExpired(true);
                if (!paymentExpiredFired.current) {
                    paymentExpiredFired.current = true;
                    sendGa4Event("payment_timeout");
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [resData?.resCreateTime]);

    Modal.setAppElement('body');

    useClarityStep('paymentProvider', {
        city: searchParameters?.location?.formatted_address || searchParameters?.pickupCity || '',
        start: searchParameters?.startDate?.toString() || '',
        end: searchParameters?.endDate?.toString() || '',
        loggedIn: loggedIn ?? false,
        paymentMethod: services.paymentMethod,
        oneway: oneWayRes,
        safeDrive: addSafeDrivePackage ?? false
    });

    React.useEffect(() => {
        let scrollPoint = document.getElementById("selectPaymentProviderScrollPoint");
        if (scrollPoint) {
          scrollPoint.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        changeVirtualPageInfo('paymentProvider');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Cosmetic 3-second loading overlay on entry to the payment screen — the
    // jump from "Reserve" straight to a populated iframe felt jarring without
    // a brief loading state. Walley /checkout + the iframe load in parallel
    // behind the modal; when the timer fires the overlay clears regardless of
    // iframe state, and the inline `walley-placeholder` spinner takes over
    // until `iframeReady`. The hard timeout (vs gating on iframeReady) is
    // deliberate: an earlier version waited for iframeReady and could get
    // stuck if the loader stalled. Cleanup on unmount prevents the parent
    // from being toggled after we've left the screen.
    useEffect(() => {
        setShowPaymentInProgress(true);
        const timer = setTimeout(() => setShowPaymentInProgress(false), 3000);
        return () => {
            clearTimeout(timer);
            setShowPaymentInProgress(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEscapePendingPayment = () => {
        sendGa4Event('CustomerDiscardedReservation');
        clearSelections();
        dispatch({type:"CLEAR_SELECTIONS",payload:null});
        setRedirectHome(true);
    }

    const redirectToSameSearch = () => {
        if (!resData) {
            setRedirectHome(true);
            return;
        }
        const baseUrl = `${window.location.origin}/`;
        const startDate = resData.startDate ? resData.startDate.replace(/@ /g, '') : '';
        const endDate = resData.endDate ? resData.endDate.replace(/@ /g, '') : '';
        const params = new URLSearchParams();

        sessionStorage.removeItem('paymentSuccess');
        sessionStorage.removeItem('rid-1');
        sessionStorage.removeItem('paymentPending');
        sessionStorage.removeItem('paytrailProviders');
        sessionStorage.removeItem('paymentProvider');
        sessionStorage.removeItem('reservationCreated');
        sessionStorage.removeItem('selectedCar');
        sessionStorage.removeItem('carIdChanged');
        sessionStorage.removeItem('carIdReservedCheck');
        sessionStorage.setItem('route', '/');
        sessionStorage.setItem('page', '0');
        if (resData.lang && resData.lang !== 'fi') params.append('lang', 'en');
        const datesInFuture = startDate && moment.tz(startDate, 'DD.MM.YYYY HH:mm', 'Europe/Helsinki').isAfter(moment.tz('Europe/Helsinki'));
        if (datesInFuture) {
            params.append('startdate', startDate);
            if (endDate) params.append('enddate', endDate);
        }
        if (resData.returnLoc) params.append('oneway', '1');
        if (resData.locationSearch) params.append('city', String(resData.locationSearch));

        window.location.href = `${baseUrl}?${params.toString()}`;
    };

    useEffect(() => {
        if (resData?.cancelled === true) {
            // reset RRM and return to home page if reservation was cancelled
            handleEscapePendingPayment();
        }
    }, []);

    // Fetch the Walley iframe payload on mount; `checkoutAttempt` lets the user
    // retry on error.
    useEffect(() => {
        if (!apiService) return;
        if (!reservationId || reservationId <= 0) {
            setWalleyError(translateService.translate('payment.walleyLoadFailed'));
            return;
        }

        let cancelled = false;
        setWalleyLoading(true);
        setWalleyError(null);

        apiService.walley.createCheckout(reservationId, { saveCard })
            .then((payload) => {
                if (cancelled) return;
                setWalleyPayload(payload);
                // NOTE: deliberately NOT calling setShowPaymentInProgress(true) here —
                // that triggered the parent's BusyModal overlay which was hiding the
                // iframe. The Walley iframe is in-page and renders its own UI; we use
                // an inline LoadingSpinner placeholder where the iframe will appear
                // until the iframe element is detected (see `iframeReady` from
                // useWalleyIframe below).
                sessionStorage.setItem('paymentPending', 'true');
                sendGa4Event('CustomerOpenedWalleyIframe');
            })
            .catch((err) => {
                if (cancelled) return;
                const errMessage = typeof err === 'string' ? err : (err?.message ?? translateService.translate('payment.walleyLoadFailed'));
                setWalleyError(errMessage);
                setWalleyPayload(null);
                sendGa4Event('payment_attempt', {
                    payment_type: 'walley',
                    payment_result: 'failure',
                    failure_stage: 'checkout_init',
                });
                // Route to the dedicated PaymentFail screen so the customer has clear
                // recovery options ("Try again", "Contact customer service") rather than
                // being trapped on the iframe shell with only a back button that wipes
                // the in-flight reservation. The reservation row itself is preserved —
                // rid-1 stays in sessionStorage and PaymentFail's retry path remounts
                // this component, which retries /walley/checkout with a fresh attempt.
                if (setShowPaymentResultScreen) {
                    setShowPaymentResultScreen(
                        errMessage === 'vehicle_no_longer_available' ? 'vehicle_unavailable' : 'fail'
                    );
                }
            })
            .finally(() => {
                if (cancelled) return;
                setWalleyLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [reservationId, checkoutAttempt, apiService]);

    const { outerRef, innerRef, iframeReady } = useWalleyIframe(
        walleyPayload,
        WALLEY_CONTAINER_ID,
        (err) => {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Walley loader script error', err);
            }
            setWalleyError(translateService.translate('payment.walleyLoadFailed'));
            setWalleyPayload(null);
        },
        deviceLanguage,
    );

    // Show the spinner while:
    //   - we're waiting on /walley/checkout, OR
    //   - we have the payload but the Walley loader script hasn't injected its iframe yet
    // Hidden once the iframe is in the DOM (or on error — the error block renders instead).
    const showIframePlaceholder = !walleyError && (walleyLoading || !walleyPayload || !iframeReady);

    if(!vehicle || !pricingDataType){
        return(
            <div className={cx(styles.r24FullWidth)}>
                <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                    <span> Vehicle Information Not Found </span>
                </div>
            </div>
        )
    }

    return <>
        <div className={cx(styles.r24FullWidth)}>
            <Modal isOpen={escapeWarningOpen} style={cStylesPaymentCreatedRes}>
                <div style={{display: 'flex', flexDirection: 'column', textAlign: "center", justifyContent: "center"}}>
                    <span>
                        {translateService.translate('paymentCallbackScreens.exitPaymentWarning')}
                    </span>
                    <button onClick={() => handleEscapePendingPayment()}
                            className='btn btn-primary btn-outline-primary btn-all m-auto mt-3 w-50'>
                        {translateService.translate('paymentCallbackScreens.yesReturnHome')}
                    </button>
                    <button onClick={() => {sendGa4Event("navigate_back", { back_to: "back_from_payment" }); setEscapeWarningOpen(false)}}
                            className='btn btn-primary btn-outline-primary btn-all m-auto mt-3 w-50'>
                        {translateService.translate('vehicleSearch.close')}
                    </button>
                </div>
            </Modal>
            <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                <table className={styles.r24PriceTable} style={{width: '100%'}}>
                    <thead>
                    <tr>
                        <th><Translate content={'vehicleSearch1.services'}/></th>
                        <th><Translate content={'vehicleSearch1.price'}/></th>
                        <th>{(couponValid || vehicle.onsale ||
                            (pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0)
                            || (vehicle?.privateData?.vip)) ?
                            <Translate content={'vehicleSearch1.discountedPrice'}/> : null }
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td> {vehicle.vehicleModelData.manufacturer} {vehicle.vehicleModelData.name} </td>
                        <td> {priceFormat(pricingDataType?.base.totalPrice / 100)} € </td>
                        <td><strong>
                            {vehicle.onsale ?
                                (priceFormat( (vehicle.onsale.price*vehicle.onsale.discountmultiplier) / 100) + ' €')
                                :
                                (couponValid && priceFormat((pricingDataType?.withCoupon?.totalPrice ?? 1) / 100) +' €')
                            }</strong>
                        </td>
                    </tr>
                    { oneWayRes ?
                            <>
                                {services.kmRange === 100 &&
                                    <>
                                        <td> <Translate content={'vehicleSearch1.onewayKmIncludedService'} />
                                            {100 * reservationLength} km.&nbsp;
                                        </td>
                                        <td><Translate content={'vehicleSearch1.includedInPrice'} />, <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km</td>
                                        {couponValid &&
                                            <td>
                                                <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>,&nbsp;
                                                <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                                            </td>
                                        }
                                        {!couponValid && <td></td>}
                                    </>

                                }
                                {(services.kmRange === 0) &&
                                    <td> <Translate content={'vehicleSearch1.unlimited'} /> km </td>}
                                {services.kmRange === 0 && <>
                                    <td>{priceFormat(pricingDataType?.base.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                                    </td>
                                    {couponValid && <td>
                                        { priceFormat(pricingDataType?.withCoupon!.unlimitedKilometers/100 * reservationLength)} &nbsp;€
                                    </td>}
                                </>}
                            </>
                            : <>
                                <tr>
                                    {(services.kmRange === 100 || services.kmRange === 400) &&
                                        <td>{services.kmRange * reservationLength /*make 0 decimals*/} km</td>}
                                    {(services.kmRange === 0 || services.kmRange === 1000) &&
                                        <td> <Translate content={'vehicleSearch1.unlimited'} /> km </td>}
                                    {services.kmRange === 100 &&
                                        <>
                                            <td><Translate content={'vehicleSearch1.includedInPrice'} />, <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km</td>
                                            {couponValid &&
                                                <td>
                                                    <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>,&nbsp;
                                                    <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                                                </td>
                                            }
                                        </>}
                                    {services.kmRange === 400 && <>
                                        <td>{priceFormat(pricingDataType?.base.pricePerKilometer / 100 * (services.kmRange - 100) * reservationLength)} &nbsp;€,&nbsp;
                                            <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km
                                        </td>
                                        {couponValid &&
                                            <td >
                                                <strong>
                                                    { priceFormat(pricingDataType?.base.pricePerKilometer/100 *
                                                        Math.max(services.kmRange - 100, 0) * reservationLength) } &nbsp;€
                                                </strong>,&nbsp; <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                                            </td>}
                                    </>}
                                    {services.kmRange === 1000 && <>
                                        <td>{priceFormat(pricingDataType?.base.pricePerKilometer / 100 * (services.kmRange - 100) * reservationLength)} &nbsp;€
                                        </td>
                                        {couponValid && <td>
                                            <strong>
                                                { priceFormat(pricingDataType?.base.pricePerKilometer/100 *
                                                    Math.max(services.kmRange - 100, 0) * reservationLength) } &nbsp;€
                                            </strong>
                                        </td>}
                                    </>}
                                    {services.kmRange === 0 && <>
                                        <td>{priceFormat(pricingDataType?.base.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                                        </td>
                                        {couponValid && <td>
                                            { priceFormat(pricingDataType?.withCoupon!.unlimitedKilometers/100 * reservationLength)} &nbsp;€
                                        </td>}
                                    </>}
                                    {!couponValid && <td></td>}
                                </tr>
                            </>
                    }

                    {services.safeDrivePackage ?
                        <ServiceRow included={services.safeDrivePackage} back={back} scrollLocation={''}
                                    label={<Translate content={'vehicleSearch1.safeDrivePackage'}/>}
                                    content={<>{priceFormat(pricingDataType?.base.safeDrivePackage / 100 * services.safeDriveReservationLength)} €</>}
                                    discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.safeDrivePackage/100 * services.safeDriveReservationLength)} €</strong>}
                                    safeDriveInfo={openSafeDriveInfo}/> : null}

                    <ServiceRow included={services.insurance.includes('1030')} back={back} scrollLocation={'insurancesAndDeductibles'}
                                label={<>
                                    <Translate content={'vehicleSearch1.fairdeductible'}/>&nbsp;
                                    {priceFormat(vehicle.vehicleData.deductible.highDeductibleValue! / 100 * (services.export?4:1))} &nbsp;€
                                </> }
                                content={<><Translate content={'vehicleSearch1.includedInPrice'}/></>}
                    />

                    <ServiceRow included={services.insurance.includes('1053')} back={back} scrollLocation={'insurancesAndDeductibles'}
                                label={<><Translate content={'vehicleSearch.services.midDeductionText'} />&nbsp;
                                    {priceFormat(vehicle.vehicleData.deductible.midDeductibleValue! / 100 * (services.export?4:1))} €</>}
                                content={<>{priceFormat(pricingDataType?.base.midDeductible / 100 * services.insuranceReservationLength)} €</>}
                                discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.midDeductible/100 * services.insuranceReservationLength)} €</strong>}
                    />

                    <ServiceRow included={services.insurance.includes('1028')} back={back} scrollLocation={'insurancesAndDeductibles'}
                                label={<>
                                    <Translate content={'vehicleSearch1.deductible'} />&nbsp;
                                    {priceFormat(vehicle.vehicleData.deductible.lowDeductibleValue! / 100 * (services.export?4:1))} €</>}
                                content={<>{priceFormat(pricingDataType?.base.lowDeductible / 100 * services.insuranceReservationLength)} €</>}
                                discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.lowDeductible/100 * services.insuranceReservationLength)} €</strong>}
                    />

                    <ServiceRow included={services.windshield} back={back} scrollLocation={'additionalInsurances'}
                                label={<Translate content={'vehicleSearch1.windshieldInsurance'} />}
                                content={<>{priceFormat(pricingDataType?.base.windshieldInsurance / 100 * services.windshieldReservationLength)} €</>}
                                discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.windshieldInsurance/100 * services.windshieldReservationLength)} €</strong>}
                    />

                    <ServiceRow included={services.tireInsurance}  back={back} scrollLocation={'additionalInsurances'}
                                label={<Translate content={'vehicleSearch1.tireInsurance'} />}
                                content={<>{priceFormat(pricingDataType?.base.tireInsurance / 100 * services.tireInsuranceReservationLength)} €</>}
                                discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.tireInsurance/100 * services.tireInsuranceReservationLength)} €</strong>}
                    />

                    {!oneWayRes ?
                        <ServiceRow included={services.cancellationInsurance} back={back} scrollLocation={'additionalInsurances'}
                                    label={<Translate content={'vehicleSearch1.cancellationInsurance'} />}
                                    content={<>{priceFormat(pricingDataType?.base.cancellationInsurance / 100 * services.cancellationInsuranceReservationLength)} €</>}
                                    discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.cancellationInsurance/100 * services.cancellationInsuranceReservationLength)} €</strong>}
                        /> : null }

                    <ServiceRow included={services.additionalDriver}   back={back} scrollLocation={'additionalDriver'}
                                label={<Translate content={'vehicleSearch1.additionalDriver'} />}
                                content={<>{priceFormat(pricingDataType?.base.additionalDriver / 100 * services.additionalDriverReservationLength)} €</>}
                                discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.additionalDriver/100 * services.additionalDriverReservationLength)} €</strong>}
                    />

                    {(vehicle.vehicleData.hasHitch && services.hook) &&
                        <ServiceRow included={services.hook} back={back}  scrollLocation={'additionalServices'}
                                    label={<Translate content={'vehicleSearch1.hook'} />}
                                    content={<>{priceFormat(pricingDataType?.base.towHitch / 100 * services.hookReservationLength)} €</>}
                                    discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.towHitch/100 * services.hookReservationLength)} €</strong>}/>
                    }

                    {services.hasHandtruck &&
                        <ServiceRow included={services.handTruck} back={back} scrollLocation={'additionalServices'}
                                    label={<Translate content={'vehicleSearch1.handTruck'} />}
                                    content={<Translate content={'vehicleSearch1.includedInPrice'} />}
                                    discount={couponValid && <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>}/>
                    }

                    {(vehicle.vehicleData.exportPermits.balticStates ||
                        vehicle.vehicleData.exportPermits.scandinavia || vehicle.vehicleData.exportPermits.europe) ?
                        <ServiceRow included={services.export} back={back}  scrollLocation={'export'}
                                    label={<Translate content={'vehicleSearch1.export'} />}
                                    content={<>{priceFormat(pricingDataType?.base.export / 100 * services.exportReservationLegth)} €</>}
                                    discount={couponValid && <strong>{ priceFormat(pricingDataType?.withCoupon!.export/100 * services.exportReservationLegth)} €</strong>}/>
                        : null
                    }

                    {services.co2compensation === '' &&
                        <tr>
                            <td><Translate content={'vehicleSearch1.co2Compensation'} /></td>
                            <td className={styles.notIncludedText}><Translate content={'vehicleSearch1.notIncluded'} /></td>
                            <td></td>
                        </tr>
                    }
                    {services.co2compensation === '1047' &&
                        <tr>
                            <td><Translate content={'vehicleSearch1.miniCompensation'} /></td>
                            <td>{priceFormat(pricingDataType?.base.hundredTwentyFiveGramsCOTwo! / 100 * services.co2CompensationReservationLength)} €</td>
                            {couponValid &&
                                <td>
                                    <strong>{ priceFormat(pricingDataType?.withCoupon!.hundredTwentyFiveGramsCOTwo!/100 * services.co2CompensationReservationLength)} €</strong>
                                </td>}
                        </tr>
                    }
                    {services.co2compensation === '1048' &&
                        <tr>
                            <td><Translate content={'vehicleSearch1.basicCompensation'} /></td>
                            <td>
                                { priceFormat(pricingDataType?.base.fiveHundredGramsCOTwo/100 * services.co2CompensationReservationLength)}
                            </td>
                            {couponValid &&
                                <td>
                                    <strong>{ priceFormat(pricingDataType?.withCoupon!.fiveHundredGramsCOTwo/100 * services.co2CompensationReservationLength)} €</strong>
                                </td>}
                        </tr>
                    }
                    {services.co2compensation === '1049' &&
                        <tr>
                            <td>
                                <Translate content={'vehicleSearch1.overCompensation'} />
                            </td>
                            <td>
                                {priceFormat(pricingDataType?.base.sevenHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                            </td>
                            {couponValid &&
                                <td>
                                    {priceFormat(pricingDataType?.withCoupon!.sevenHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                                </td>
                            }
                        </tr>
                    }
                    {couponValid &&
                        <tr>
                            <td> <Translate content={'vehicleSearch1.discount'}/> {vehicle.vehicleData.class === 0 ? ' (SmartSaver)' : null}</td>
                            <td> 0 €</td>
                            <td>
                                <strong> -{priceFormat(discountValue(vehicle, noVatPrices, services,reservationLength)) } €</strong>
                            </td>
                        </tr>
                    }
                    {useTwoFourPoints && pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0 ?
                        <tr>
                            <td><strong>{translateService.translate('payment.twoFourPoints')}</strong></td>
                            <td> 0 €</td>
                            <td><strong>-{priceFormat(pointsInEurosToBeApplied ?? 0)} € </strong></td>
                        </tr>
                        : null}
                    <tr>
                        <td><strong>
                            <Translate with={{ type: noVatPrices // vat from vehicle data 0% or 25%, unchnageable at this stage
                                    ? translateService.translate('vehicleSearch.vatPercent0') :
                                    translateService.translate('vehicleSearch.vatPercent25')}}
                                       content='vehicleSearch1.totalVat'  />
                        </strong></td>
                        <td>
                            {priceFormat(basePrice(vehicle, noVatPrices, services, reservationLength))} €
                        </td>
                        <td >
                            {
                                vehicle.onsale ?
                                    <strong>{priceFormat(baseOnSalesDiscountPrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} €</strong>
                                    : ( couponValid ?
                                            <strong>{priceFormat(discountPrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} €</strong>
                                            :
                                            pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0 ?
                                                <strong>{priceFormat(basePrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} € </strong>
                                                : null
                                    )
                            }
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
            {paymentExpired ? (
                <div className='callback-info-container pb-3' id='paymentExpiredScrollPoint'>
                    <div className='payment-callback-section py-3 mobile-top-line'>
                        <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                            <div className={cx(styles['payment-footer-lastminutenotice-expired'], styles.center)}>
                                <span className={styles.lastminutecounternotice1}>
                                    {translateService.translate('paymentCallbackScreens.paymentExpiredHeading')}
                                </span>
                                <div className={styles.lastminutecounternotice2}>
                                    {translateService.translate('paymentCallbackScreens.paymentExpiredText')}
                                </div>
                            </div>
                        </div>
                        <div className='d-flex justify-content-center mb-2 mt-2'>
                            <button className="btn btn-primary btn-outline-primary btn-cta mt-2"
                                    onClick={() => redirectToSameSearch()}>
                                {translateService.translate('paymentCallbackScreens.resCancelledBtn1')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {paymentTimeRemaining !== null && (
                        <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                            <div className={cx(
                                paymentTimeRemaining <= 60
                                    ? styles['payment-footer-lastminutenotice-urgent']
                                    : styles['payment-footer-lastminutenotice'],
                                styles.center
                            )}>
                                <span className={styles.lastminutecounternotice1}>
                                    <strong className={styles.countdownTimeLine}>{paymentTimeRemaining % 2 === 0 ? '⏳' : '⌛'} {`${String(Math.floor(paymentTimeRemaining / 60)).padStart(2, '0')}:${String(paymentTimeRemaining % 60).padStart(2, '0')}`}</strong> &nbsp;{translateService.translate('paymentCallbackScreens.paymentCountdownRemaining')}
                                    <br />
                                    {paymentTimeRemaining <= 60
                                        ? translateService.translate('paymentCallbackScreens.paymentCountdownUrgent')
                                        : translateService.translate('paymentCallbackScreens.paymentCountdownText')}
                                </span>
                                <div className={styles.lastminutecounternotice2}>
                                    {translateService.translate('paymentCallbackScreens.paymentCountdownNotice')}
                                </div>
                            </div>
                        </div>
                    )}
                    <span id={"selectPaymentProviderScrollPoint"}>{translateService.translate('paymentCallbackScreens.selectPaymentProvider')}</span>
                    {walleyError && !walleyPayload && (
                        <div className="walley-error">
                            <span>{walleyError}</span>
                            <button type="button"
                                    className={cx("btn btn-all", styles.orangeButton)}
                                    onClick={() => setCheckoutAttempt(n => n + 1)}>
                                {translateService.translate('payment.retryLoadingPayment')}
                            </button>
                        </div>
                    )}
                    {/*
                      Wrapper is position:relative so the placeholder can sit absolutely
                      on top of the walley-outer (which must stay in normal flow so the
                      loader script can measure offsetWidth and inject the iframe with
                      correct dimensions). Once `iframeReady` flips true, the placeholder
                      unmounts and the iframe is revealed underneath.
                    */}
                    <div className={`walley-outer-wrapper${showIframePlaceholder ? ' walley-outer-wrapper--loading' : ''}`}>
                        <div ref={outerRef} className="walley-outer">
                            <div ref={innerRef} id={WALLEY_CONTAINER_ID} className="walley-inner" />
                        </div>
                        {showIframePlaceholder && (
                            <div className="walley-placeholder">
                                <LoadingSpinner />
                            </div>
                        )}
                    </div>

                    <div className={cx('d-flex min-width-320', styles.r24FullWidth, styles.r24Padding10, styles.r24FullWidthFlexPageSelection)}>
                        <div className={cx('flex-grow-1', styles.r24ChildFlex1, styles.r24RightMargin5)}>
                          <button type="button" className={cx("btn btn-all", styles.greenBackButton)} onClick={() => {
                                        sendGa4Event("navigate_back", { back_to: "back_from_payment" });
                                        setEscapeWarningOpen(true);
                              }}>
                                        <ArrowBackIcon className={styles.arrowIcon} />
                          </button>
                        </div>
                        {/* Right side intentionally empty: the Walley iframe owns its own pay button. */}
                        <div className={cx('flex-grow-1', styles.r24ChildFlex4, styles.r24LeftMargin5)} />
                    </div>
                </>
            )}
        </div>
    </>
}
